import { useEffect, useMemo, useRef, useState } from 'react';

import type { ToothMassCentroidMm } from '@/shared/graphics/medicalImageRender/MedicalImageRender';
import { useAppDispatch, useAppSelector } from '@/shared/hooks';

import { reportsModel, useCheckReportSignature } from '@/entities/reports';

import type { FullHeadMprReportSelectionResolver } from '@/features/MPR';

import type {
  FullHeadMprSliceGroupView,
  FullHeadMprSliceRecordViewsByTileID,
  FullHeadMprSliceTileView,
  ToothExtentsProfiles,
  ToothSliceGeometry,
  ToothSliceOptionsBySeries,
} from '../config';
import {
  loadFullHeadMprOriginalSlice,
  makeDefaultFullHeadMprSliceSelection,
  makeFullHeadMprSliceGeometry,
  makeFullHeadMprSliceTarget,
  resolveEffectiveFullHeadMprSliceSelection,
  resolveFullHeadMprSeriesTile,
} from '../lib';

import { useFullHeadMprReportTiles } from './useFullHeadMprReportTiles';

export const useFullHeadMprSliceSelection = ({
  fullHeadMprSliceGroups,
  input,
  massCentroidMm,
  profiles,
  recordsByTileID,
  sliceOptions,
  toothID,
  isSelectionReady = true,
  isEnabled = true,
  onClearSelection,
}: {
  fullHeadMprSliceGroups: FullHeadMprSliceGroupView[];
  input: ToothSliceGeometry | null;
  massCentroidMm: ToothMassCentroidMm | null;
  profiles: ToothExtentsProfiles | null;
  recordsByTileID: FullHeadMprSliceRecordViewsByTileID;
  sliceOptions: ToothSliceOptionsBySeries;
  toothID: string;
  isSelectionReady?: boolean;
  isEnabled?: boolean;
  onClearSelection?: () => Promise<void>;
}) => {
  const dispatch = useAppDispatch();
  const report = useAppSelector(reportsModel.selectors.selectCurrentReport);
  const mprLoading = useAppSelector(
    reportsModel.selectors.selectFullHeadMprLoading(report?.ID ?? ''),
  );
  const hasLoadedSelection = mprLoading === 'succeeded';
  const canChangeSelection =
    hasLoadedSelection && !!report?.YourPermissions?.CanChangeToothAssets;

  const { checkReportSignature } = useCheckReportSignature();

  const [pendingByTileID, setPendingByTileID] = useState<
    Record<string, boolean>
  >({});

  const [isClearingSelection, setIsClearingSelection] = useState(false);
  const clearingSelectionRef = useRef(false);
  const [openedTile, setOpenedTile] = useState<{
    tile: FullHeadMprSliceTileView;
    toothID: string;
  } | null>(null);
  const zoomedTile = openedTile?.toothID === toothID ? openedTile.tile : null;

  useEffect(() => {
    setOpenedTile(null);
  }, [toothID]);

  const defaultTiles = useMemo(
    () =>
      makeDefaultFullHeadMprSliceSelection({
        groups: fullHeadMprSliceGroups,
        massCentroidMm,
        profiles,
      }),
    [fullHeadMprSliceGroups, massCentroidMm, profiles],
  );

  const {
    groups: renderedGroups,
    hasSavedViews,
    savedViewsLoading,
    tiles,
  } = useFullHeadMprReportTiles({
    groups: fullHeadMprSliceGroups,
    isEnabled,
    recordsByTileID,
    toothID,
  });

  const { isTileSelected, selectedTiles } = useMemo(
    () =>
      resolveEffectiveFullHeadMprSliceSelection({
        defaultTiles,
        recordsByTileID,
        tiles,
      }),
    [defaultTiles, recordsByTileID, tiles],
  );

  const pendingTileIDs = Object.keys(pendingByTileID);

  const submitTileSelection = async (
    tile: FullHeadMprSliceTileView,
    isAdded: boolean,
  ) => {
    if (!canChangeSelection) {
      return;
    }
    const record = recordsByTileID[tile.id];
    const group = fullHeadMprSliceGroups.find(
      (clientGroup) => clientGroup.seriesName === tile.seriesName,
    );

    if (tile.kind !== 'view' && (!input || !group)) {
      return;
    }

    setPendingByTileID((current) => ({ ...current, [tile.id]: true }));

    try {
      await dispatch(
        reportsModel.thunks.setToothFullHeadMPRSliceSelection({
          ...(record
            ? {
                ToothID: toothID,
                Target: { case: 'SliceID' as const, value: record.recordID },
              }
            : makeFullHeadMprSliceTarget({
                tile,
                toothID,
                sliceStepMm: group!.options.sliceStepMm,
              })),
          ...(record || tile.kind === 'view'
            ? {}
            : {
                Geometry: makeFullHeadMprSliceGeometry({
                  geometrySource: input!.geometrySource,
                  heightPx: group!.frame.heightPx,
                  scaleMmPerPx: group!.frame.spacingMm,
                  thicknessMm: group!.slabThicknessMm,
                  volumeFileID: input!.volumeFileID,
                  widthPx: group!.frame.widthPx,
                }),
              }),
          IsAdded: isAdded,
        }),
      );
    } finally {
      setPendingByTileID((current) =>
        Object.fromEntries(
          Object.entries(current).filter(([tileID]) => tileID !== tile.id),
        ),
      );
    }
  };

  const toggleTile = (tile: FullHeadMprSliceTileView) => {
    if (
      !canChangeSelection ||
      !isSelectionReady ||
      pendingByTileID[tile.id] ||
      clearingSelectionRef.current
    ) {
      return;
    }

    checkReportSignature({
      toothID,
      onSignatureChecked: () =>
        submitTileSelection(tile, !isTileSelected(tile)),
    });
  };

  const resolveViewSelection: FullHeadMprReportSelectionResolver = (
    snapshot,
  ) => {
    const tile = resolveFullHeadMprSeriesTile({
      groups: fullHeadMprSliceGroups,
      input,
      snapshot,
    });
    const group = tile
      ? fullHeadMprSliceGroups.find(
          (candidate) => candidate.seriesName === tile.seriesName,
        )
      : null;
    return tile && group
      ? {
          isDisabled:
            !canChangeSelection || !isSelectionReady || isClearingSelection,
          isLoading: !!pendingByTileID[tile.id],
          isSelected: hasLoadedSelection && isTileSelected(tile),
          getOriginalSlice: () =>
            loadFullHeadMprOriginalSlice({
              group,
              input,
              record: recordsByTileID[tile.id],
              sliceOptions,
              tile,
              toothID,
            }),
          onToggle: () => toggleTile(tile),
        }
      : null;
  };

  const clearSelection = () => {
    if (
      !canChangeSelection ||
      !isSelectionReady ||
      savedViewsLoading !== 'succeeded' ||
      clearingSelectionRef.current ||
      pendingTileIDs.length
    ) {
      return;
    }

    clearingSelectionRef.current = true;
    setIsClearingSelection(true);

    const finish = () => {
      clearingSelectionRef.current = false;
      setIsClearingSelection(false);
    };

    checkReportSignature({
      toothID,
      onCancel: finish,
      onSignatureChecked: async () => {
        try {
          await selectedTiles.reduce(async (previousSubmit, tile) => {
            await previousSubmit;
            await submitTileSelection(tile, false);
          }, Promise.resolve());
          await onClearSelection?.();
        } finally {
          finish();
        }
      },
    });
  };

  return {
    fullHeadMprSliceGroups: renderedGroups,
    hasSavedViews,
    savedViewsLoading,
    tiles,
    clearSelection,
    isClearingSelection,
    isTileSelected: (tile: FullHeadMprSliceTileView) =>
      hasLoadedSelection && isTileSelected(tile),
    pendingTileIDs,
    resolveViewSelection,
    selectedTiles: hasLoadedSelection ? selectedTiles : [],
    setZoomedTileID: (tileID: string) => {
      const tile = tiles.find((candidate) => candidate.id === tileID);
      setOpenedTile(tile ? { tile, toothID } : null);
    },
    toggleTile,
    zoomedTile,
    zoomedTileID: zoomedTile?.id ?? '',
  };
};
